/**
 * Funções utilitárias para o Sidebar do SIDEB
 */

/**
 * Gera as iniciais do nome do usuário para exibição no avatar
 * @param {string} name - Nome do usuário
 * @returns {string} Iniciais em maiúsculas (máximo 2 caracteres)
 */
export const getInitials = (name) => {
  if (!name) return '??';
  return name
    .split(' ')
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

/**
 * Retorna as classes CSS para o badge de role do usuário
 * @param {string} role - Role do usuário (gestor, coordenador, gerente, admin)
 * @param {boolean} darkMode - Se está no modo escuro
 * @returns {string} Classes CSS para o badge
 */
export const getBadgeClass = (role, darkMode) => {
  const badges = {
    gestor: darkMode
      ? 'bg-sideb-blue/20 text-sideb-blue'
      : 'bg-[#0974BA]/10 text-[#0974BA]',
    diretor: darkMode
      ? 'bg-sideb-yellow/20 text-sideb-yellow'
      : 'bg-[#F9B047]/10 text-[#F9B047]',
    gerente: darkMode
      ? 'bg-sideb-yellow/20 text-sideb-yellow'
      : 'bg-[#F9B047]/10 text-[#F9B047]',
    coordenador: darkMode
      ? 'bg-sideb-green/20 text-sideb-green'
      : 'bg-[#13983D]/10 text-[#13983D]',
    admin: darkMode
      ? 'bg-sideb-red/20 text-sideb-red'
      : 'bg-[#E83A3C]/10 text-[#E83A3C]',
    administrador: darkMode
      ? 'bg-sideb-red/20 text-sideb-red'
      : 'bg-[#E83A3C]/10 text-[#E83A3C]',
  };
  return badges[role] || badges.gestor;
};

/**
 * Retorna os estilos inline do container do sidebar
 * @param {boolean} darkMode - Se está no modo escuro
 * @returns {Object} Objeto de estilos
 */
export const getSidebarStyles = (darkMode) => ({
  scrollbarWidth: 'thin',
  scrollbarColor: darkMode ? '#334155 transparent' : '#cbd5e1 transparent'
});

/**
 * Retorna as classes CSS do container do sidebar
 *
 * No celular o menu é uma gaveta: fica fora da tela e entra por cima do
 * conteúdo quando aberto. Antes ele era fixo em 260px em qualquer largura,
 * o que num aparelho de 390px deixava a página com pouco mais de 100px —
 * títulos quebrando letra a letra e filtros espremidos.
 *
 * "Colapsado" continua sendo um conceito só de desktop: na gaveta o menu
 * abre sempre inteiro, porque lá o espaço não é disputado com a página.
 *
 * @param {boolean} darkMode - Se está no modo escuro
 * @param {boolean} collapsed - Se está colapsado (a partir de md)
 * @param {boolean} mobileOpen - Se a gaveta está aberta (abaixo de md)
 * @returns {string} Classes CSS
 */
export const getSidebarClasses = (darkMode, collapsed = false, mobileOpen = false) => {
  const width = collapsed ? 'w-[280px] md:w-[80px]' : 'w-[280px] md:w-[260px]';
  const padding = collapsed ? 'p-6 md:p-3' : 'p-6';
  const slide = mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0';

  return `fixed left-0 top-0 bottom-0 max-w-[85vw] ${width} ${padding} ${slide} border-r flex flex-col z-50 overflow-y-auto scrollbar-thin transition-transform md:transition-all duration-300 ${
    darkMode
      ? 'bg-slate-900/95 border-slate-700/10 scrollbar-thumb-slate-700 scrollbar-track-transparent'
      : 'bg-white/95 border-slate-200 shadow-lg scrollbar-thumb-slate-300 scrollbar-track-transparent'
  }`;
};
